import React from 'react';
import { 
  Cpu,
  Activity,
  Shield,
  Settings,
  BarChart,
  Workflow,
  CloudCog,
  MonitorCheck,
  TimerReset,
  Phone
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import img from "../../Images/boi.png"
import img1 from "../../Images/boi1.webp"

const BoilerAutomationPage = () => {
  const services = [
    {
      title: "Smart Control Systems",
      description: "Advanced PLC-based control systems with real-time monitoring and intelligent optimization for improved boiler efficiency.",
      icon: <Cpu className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
    },
    {
      title: "Remote Monitoring",
      description: "24/7 remote monitoring capabilities with instant alerts, predictive maintenance, and performance analytics.",
      icon: <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
    },
    {
      title: "Safety Integration",
      description: "Comprehensive safety systems integration with fail-safe mechanisms and emergency protocols.",
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
    }
  ];

  const features = [
    {
      title: "Automated Combustion Control",
      description: "Optimize fuel-air ratio automatically for maximum efficiency"
    },
    {
      title: "Load Management",
      description: "Smart load balancing and demand response capabilities"
    },
    {
      title: "Performance Analytics",
      description: "Real-time efficiency monitoring and reporting"
    },
    {
      title: "Predictive Maintenance",
      description: "AI-driven maintenance scheduling and fault prediction"
    }
  ];

  const benefits = [
    {
      icon: <BarChart className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />,
      title: "Increased Efficiency",
      description: "Up to 15% reduction in fuel consumption through precise control"
    },
    {
      icon: <TimerReset className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />,
      title: "Reduced Downtime",
      description: "Predictive maintenance reduces unexpected shutdowns"
    },
    {
      icon: <MonitorCheck className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />,
      title: "Enhanced Safety",
      description: "Advanced safety protocols and real-time monitoring"
    }
  ];

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Industrial Boiler Automation Solutions",
    "description": "Advanced boiler automation systems with PLC control, remote monitoring, and safety integration for improved efficiency and reliability",
    "provider": {
      "@type": "Organization",
      "name": "Gortherm Energy",
      "url": "https://www.gorthermenergy.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Nepal"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Boiler Automation Services",
      "itemListElement": services.map((service) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.title,
          "description": service.description
        }
      }))
    }
  };

  return (
    <>
      <Helmet>
        <title>Industrial Boiler Automation & Control Systems | PLC, SCADA & Remote Monitoring | Gortherm Energy</title>
        <meta name="description" content="Advanced industrial boiler automation solutions including PLC-based control systems, remote monitoring, SCADA integration, and predictive maintenance. Improve efficiency, safety, and reduce operational costs with smart boiler automation." />
        <meta name="keywords" content="boiler automation, PLC control systems, SCADA, remote monitoring, industrial automation, boiler control, predictive maintenance, combustion control, boiler efficiency, automation Nepal, smart boiler systems" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Industrial Boiler Automation & Control Systems | Gortherm Energy" />
        <meta property="og:description" content="Transform your boiler operations with state-of-the-art automation solutions. PLC control, remote monitoring, and predictive maintenance." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/boiler-automation" />
        <meta property="og:image" content={img1} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial Boiler Automation Solutions | Gortherm Energy" />
        <meta name="twitter:description" content="Smart boiler control systems with remote monitoring and predictive maintenance for improved efficiency." />
        <meta name="twitter:image" content={img1} />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/boiler-automation" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="w-full">
        {/* Hero Section */}
        <header className="relative h-64 sm:h-80 lg:h-96 bg-gradient-to-r from-slate-900 to-indigo-900 overflow-hidden pt-16 sm:pt-20">
          <div className="absolute inset-0">
            <img
              src={img1}
              alt="Industrial boiler automation and control systems by Gortherm Energy"
              className="w-full h-full object-cover opacity-20"
              loading="eager"
              width="1920"
              height="400"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">
                Industrial Boiler Automation
              </h1>
              <p className="text-base sm:text-xl lg:text-2xl mb-6 sm:mb-8">
                Transform your boiler operations with state-of-the-art automation solutions from Nepal's leading industrial technology partner.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a href="/contact">
                  <button className="bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg flex items-center justify-center gap-2 transition-all text-sm sm:text-base font-semibold w-full sm:w-auto">
                    Request Demo
                    <Settings className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                  </button>
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Services Grid */}
          <section className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Automation Solutions</h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-3xl mx-auto px-4">
              Advanced boiler automation systems designed to enhance efficiency, safety, and reliability 
              while reducing operational costs.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {services.map((service, index) => (
              <article 
                key={index}
                className="p-4 sm:p-6 rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-4" aria-hidden="true">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{service.description}</p>
              </article>
            ))}
          </div>

          {/* Features Grid */}
          <section className="mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center">Key Features</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              {features.map((feature, index) => (
                <div key={index} className="relative">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <span className="text-blue-600 font-bold text-sm sm:text-base">{index + 1}</span>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold mb-2 text-center">{feature.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-600 text-center">{feature.description}</p>
                  </div>
                  {index < features.length - 1 && (
                    <div className="hidden md:block absolute top-6 left-[60%] w-[80%] h-[2px] bg-blue-100"></div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Technology Showcase */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16">
            <div className="relative h-64 sm:h-80 md:h-96 order-2 md:order-1">
              <img 
                src={img}
                alt="Smart boiler automation control interface and monitoring dashboard"
                className="rounded-lg shadow-xl object-cover h-full w-full"
                loading="lazy"
                width="600"
                height="400"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Smart Control Interface</h2>
              <div className="space-y-4 sm:space-y-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3 sm:gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">{benefit.title}</h3>
                      <p className="text-sm sm:text-base text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Integration Section */}
          <section className="bg-gray-50 rounded-xl p-6 sm:p-8 mb-12 sm:mb-16">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Seamless Integration</h2>
              <p className="text-sm sm:text-base text-gray-600 px-4">
                Our automation solutions integrate seamlessly with existing boiler systems and industrial infrastructure.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              <div className="flex flex-col items-center p-4 sm:p-6 bg-white rounded-lg shadow-sm">
                <CloudCog className="w-10 h-10 sm:w-12 sm:h-12 text-blue-600 mb-3 sm:mb-4" aria-hidden="true" />
                <h3 className="font-semibold mb-2 text-base sm:text-lg">Cloud Connectivity</h3>
                <p className="text-xs sm:text-sm text-gray-600 text-center">Secure cloud-based monitoring and control capabilities</p>
              </div>
              <div className="flex flex-col items-center p-4 sm:p-6 bg-white rounded-lg shadow-sm">
                <Workflow className="w-10 h-10 sm:w-12 sm:h-12 text-blue-600 mb-3 sm:mb-4" aria-hidden="true" />
                <h3 className="font-semibold mb-2 text-base sm:text-lg">Process Integration</h3>
                <p className="text-xs sm:text-sm text-gray-600 text-center">Seamless integration with existing industrial processes</p>
              </div>
              <div className="flex flex-col items-center p-4 sm:p-6 bg-white rounded-lg shadow-sm">
                <Settings className="w-10 h-10 sm:w-12 sm:h-12 text-blue-600 mb-3 sm:mb-4" aria-hidden="true" />
                <h3 className="font-semibold mb-2 text-base sm:text-lg">Customization</h3>
                <p className="text-xs sm:text-sm text-gray-600 text-center">Tailored solutions for your specific operational needs</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <aside className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Ready to Automate Your Boiler System?
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Schedule a consultation with our automation experts and discover how smart controls can transform your operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact">
                <button className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg font-semibold text-sm sm:text-base lg:text-lg transition-all duration-300 shadow-md hover:shadow-xl w-full sm:w-auto">
                  Request Consultation
                </button>
              </a>
              <a 
                href="tel:+9779802781400"
                className="inline-block"
                aria-label="Contact boiler automation experts at +977 980 278 1400"
              >
                <button className="bg-white hover:bg-gray-50 text-blue-600 border-2 border-blue-600 px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold w-full sm:w-auto justify-center">
                  <Phone className="h-5 w-5 sm:h-6 sm:h-6" aria-hidden="true" />
                  <span>Call Our Experts</span>
                </button>
              </a>
            </div>
           
          </aside>
        </div>
      </main>
    </>
  );
};

export default BoilerAutomationPage;